import { useCallback, useState } from "react";
import { useAppContext } from "../context/AppContext";
import { createFloor, patchFloor } from "../services/floorService";
import { toast } from "../utils/toast";

/**
 * Shared form logic for the Floor create/edit pages.
 *
 * Holds the common { building_id, floor_code, name } form state, validation,
 * the create-vs-patch save branch, and the "back to list" navigation with the
 * unassigned-entities returnTo handling. Data loading (company/building lists
 * for create, floor-with-relations for edit) stays in the individual pages.
 *
 * @param {Object} opts
 * @param {"create"|"edit"} opts.mode
 * @param {number|null} opts.floorId  Required in edit mode.
 */
export function useFloorForm({ mode, floorId = null }) {
  const { setActiveSection, setFloorId } = useAppContext();
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    building_id: "",
    floor_code: "",
    name: "",
  });
  const [saving, setSaving] = useState(false);

  const backToList = useCallback(() => {
    try {
      localStorage.removeItem("floorEditId");
    } catch (_) {}
    setFloorId(null);

    // Return to Unassigned Entities if that's where we came from.
    const returnTo = localStorage.getItem("returnTo");
    if (returnTo === "unassigned-entities") {
      const returnToTab = localStorage.getItem("returnToTab");
      if (returnToTab) {
        localStorage.setItem("unassignedEntitiesActiveTab", returnToTab);
        localStorage.removeItem("returnToTab");
      }
      localStorage.removeItem("returnTo");
      setActiveSection("unassigned-entities");
    } else {
      setActiveSection("building-floors");
    }
  }, [setActiveSection, setFloorId]);

  /**
   * Validates and persists the floor.
   *
   * Returns false on failure, and on success the saved floor — the create path
   * needs its new id so the page can open the design layout for it rather than
   * making the user find the floor again.
   *
   * @param {Object} [extra] Extra behavior flags.
   * @param {boolean} [extra.navigateOnSuccess=true] Navigate back after save.
   */
  const save = useCallback(
    async ({ navigateOnSuccess = true } = {}) => {
      const bId = Number(form.building_id);
      const code = String(form.floor_code || "").trim();
      const name = String(form.name || "").trim();

      if (!Number.isInteger(bId) || bId <= 0) {
        toast.error("Please select a building");
        return false;
      }
      if (!code) {
        toast.error("Floor Code is required");
        return false;
      }
      if (!name) {
        toast.error("Floor Name is required");
        return false;
      }

      let saved = null;
      try {
        setSaving(true);
        if (isEdit) {
          if (floorId == null) {
            toast.error("No floor selected");
            return false;
          }
          saved = await patchFloor(floorId, { floorCode: code, name });
          toast.success(`Floor "${name}" has been updated successfully!`);
        } else {
          saved = await createFloor({ building_id: bId, floorCode: code, name });
          toast.success(`Floor "${name}" has been created successfully!`);
        }

        if (navigateOnSuccess) {
          const returnTo = localStorage.getItem("returnTo");
          if (returnTo === "unassigned-entities") {
            const returnToTab = localStorage.getItem("returnToTab");
            if (returnToTab) {
              localStorage.setItem("unassignedEntitiesActiveTab", returnToTab);
              localStorage.removeItem("returnToTab");
            }
            localStorage.removeItem("returnTo");
            setTimeout(() => setActiveSection("unassigned-entities"), 600);
          } else {
            backToList();
          }
        }
        return saved ?? true;
      } catch (e) {
        toast.error(e?.message || "Failed to save floor");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [form, isEdit, floorId, backToList, setActiveSection],
  );

  return { form, setForm, saving, save, backToList };
}

export default useFloorForm;
